<script>
/**
 * AudioPlayer — player customizado pra mensagens de voz no chat.
 * Estilo WhatsApp Web/Telegram: botão play/pause + waveform animada +
 * tempo + velocidade de reprodução.
 */
const VELOCIDADES = [1, 1.5, 2];
const NUM_BARRAS = 32;

export default {
  name: 'AudioPlayer',
  props: {
    src: { type: String, required: true },
    invertido: { type: Boolean, default: false },
  },
  data() {
    return {
      audio: null,
      tocando: false,
      duracao: 0,
      progresso: 0,
      velocidadeIdx: 0,
      carregando: true,
      // Waveform pseudo-random fixa (mesmas alturas pra mesma URL = consistente)
      barras: this._gerarBarras(),
    };
  },
  computed: {
    percentual() {
      if (!this.duracao) return 0;
      return (this.progresso / this.duracao) * 100;
    },
    velocidade() { return VELOCIDADES[this.velocidadeIdx]; },
    duracaoFormatada() { return this._formatar(this.duracao); },
    progressoFormatado() { return this._formatar(this.progresso); },
    barraAtivaIdx() {
      if (!this.duracao) return -1;
      return Math.floor((this.progresso / this.duracao) * NUM_BARRAS);
    },
  },
  watch: {
    src() { this._reset(); this._inicializar(); },
  },
  mounted() { this._inicializar(); },
  beforeUnmount() { this._destruir(); },
  methods: {
    _inicializar() {
      // Captura o audio local; os listeners usam essa referência ao invés de
      // `this.audio` pra evitar TypeError quando o componente desmonta
      // (this.audio = null) mas eventos pendentes ainda disparam.
      const a = new Audio(this.src);
      a.preload = 'metadata';
      this.audio = a;

      this._onLoaded = () => {
        if (this.audio !== a) return;
        this.duracao = a.duration || 0;
        this.carregando = false;
      };
      this._onTime = () => {
        if (this.audio !== a) return;
        this.progresso = a.currentTime;
      };
      this._onEnded = () => {
        if (this.audio !== a) return;
        this.tocando = false;
        this.progresso = 0;
      };
      this._onError = () => {
        if (this.audio !== a) return;
        this.carregando = false;
      };

      a.addEventListener('loadedmetadata', this._onLoaded);
      a.addEventListener('timeupdate', this._onTime);
      a.addEventListener('ended', this._onEnded);
      a.addEventListener('error', this._onError);
    },
    _destruir() {
      const a = this.audio;
      if (!a) return;
      try {
        a.pause();
        if (this._onLoaded) a.removeEventListener('loadedmetadata', this._onLoaded);
        if (this._onTime) a.removeEventListener('timeupdate', this._onTime);
        if (this._onEnded) a.removeEventListener('ended', this._onEnded);
        if (this._onError) a.removeEventListener('error', this._onError);
        a.src = '';
        a.load();
      } catch (_e) { /* noop */ }
      this.audio = null;
    },
    _reset() {
      // Limpa o audio anterior por completo antes de criar o próximo,
      // evitando que listeners velhos disparem com referências inválidas.
      this._destruir();
      this.tocando = false;
      this.progresso = 0;
      this.duracao = 0;
      this.carregando = true;
    },
    togglePlay() {
      if (!this.audio) return;
      if (this.tocando) {
        this.audio.pause();
        this.tocando = false;
      } else {
        this.audio.play().catch(() => {});
        this.tocando = true;
      }
    },
    onSeek(event) {
      const el = this.$refs.waveform;
      if (!el || !this.audio || !this.duracao) return;
      const rect = el.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      this.audio.currentTime = ratio * this.duracao;
      this.progresso = this.audio.currentTime;
    },
    trocarVelocidade() {
      this.velocidadeIdx = (this.velocidadeIdx + 1) % VELOCIDADES.length;
      if (this.audio) this.audio.playbackRate = this.velocidade;
    },
    _formatar(seg) {
      if (!seg || Number.isNaN(seg) || !Number.isFinite(seg)) return '0:00';
      const m = Math.floor(seg / 60);
      const s = Math.floor(seg % 60);
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    _gerarBarras() {
      // Heights pseudo-aleatórias mas determinísticas (visual de "waveform")
      const out = [];
      for (let i = 0; i < NUM_BARRAS; i += 1) {
        // Pico no meio, baixinho nas pontas — perfil de voz típico
        const dist = Math.abs(i - NUM_BARRAS / 2) / (NUM_BARRAS / 2);
        const variacao = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * 0.3 + 0.5;
        const h = Math.max(0.18, (1 - dist * 0.6) * variacao);
        out.push(h);
      }
      return out;
    },
  },
};
</script>

<template>
  <div class="audio-player" :class="{ invertido }">
    <div class="audio-topo">
      <button
        type="button"
        class="btn-play"
        :title="tocando ? 'Pausar' : 'Tocar'"
        :disabled="carregando"
        @click="togglePlay"
      >
        <i v-if="carregando" class="ri-loader-4-line spin"></i>
        <i v-else-if="tocando" class="ri-pause-fill"></i>
        <i v-else class="ri-play-fill"></i>
      </button>

      <div ref="waveform" class="waveform" @click="onSeek">
        <div
          v-for="(h, i) in barras"
          :key="i"
          class="bar"
          :class="{ ativa: i <= barraAtivaIdx }"
          :style="{ height: `${h * 100}%` }"
        ></div>
      </div>

      <span class="tempo">{{ tocando || progresso ? progressoFormatado : duracaoFormatada }}</span>
    </div>

    <div class="audio-rodape">
      <button
        type="button"
        class="btn-velocidade"
        :class="{ destaque: velocidade !== 1 }"
        title="Velocidade de reprodução"
        @click="trocarVelocidade"
      >
        {{ velocidade }}x
      </button>
      <div class="audio-rodape-direita">
        <slot name="footer-right"></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audio-player {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  min-width: 200px;
  max-width: 100%;
}

.audio-topo,
.audio-rodape {
  display: flex;
  align-items: center;
  width: 100%;
}

.audio-topo {
  gap: 0.5rem;
  min-height: 32px;
}

.audio-rodape {
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 18px;
}

.btn-play {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  background: var(--vz-primary);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.1s, background 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  padding: 0;
}
.btn-play:hover:not(:disabled) { transform: scale(1.08); }
.btn-play:active:not(:disabled) { transform: scale(0.94); }
.btn-play:disabled { opacity: 0.6; cursor: progress; }

.audio-player.invertido .btn-play {
  background: #fff;
  color: var(--vz-primary);
}

.waveform {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1px;
  height: 24px;
  cursor: pointer;
}

.bar {
  flex: 1;
  background: rgba(135, 135, 135, 0.14);
  border-radius: 1px;
  min-height: 3px;
  transition: background 0.15s, height 0.15s;
}
.bar.ativa { background: #8ea2b8; }

.audio-player.invertido .bar { background: rgba(255, 255, 255, 0.4); }
.audio-player.invertido .bar.ativa { background: #fff; }

.tempo {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  font-size: 0.68rem;
  opacity: 0.8;
  min-width: 32px;
  text-align: right;
}

.btn-velocidade {
  flex: 0 0 auto;
  background: rgba(0, 0, 0, 0.08);
  border: 0;
  color: inherit;
  padding: 0.05rem 0.35rem;
  border-radius: 0.65rem;
  font-size: 0.6rem;
  font-weight: 600;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  transition: background 0.15s;
  line-height: 1.2;
}
.btn-velocidade:hover { background: rgba(0, 0, 0, 0.16); }
.btn-velocidade.destaque {
  background: var(--vz-primary);
  color: #fff;
}
.audio-player.invertido .btn-velocidade { background: rgba(255, 255, 255, 0.2); }
.audio-player.invertido .btn-velocidade:hover { background: rgba(255, 255, 255, 0.3); }
.audio-player.invertido .btn-velocidade.destaque { background: #fff; color: var(--vz-primary); }

.audio-rodape-direita {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
}

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
